import { supabase, isConfigured, requireClient } from './supabase.js';
import { currentUser } from './auth.js';

/* Administering Motion licences.

   Every function here answers to the same RLS the app does — there is no
   service-role key in the browser and there is not meant to be one. An admin
   is an email in `feedback_admins` (0007), and `motion_licences`'s admin
   policy (0010) is what actually permits these writes; isAdmin() below only
   decides whether it is worth putting the screen on screen at all. Someone
   who fakes it client-side gets a screen whose every query is refused.

   Password resets are deliberately NOT in that category: resetPasswordForEmail
   is a public endpoint (see services/auth.js), so the owner sending one is
   the same call the customer could make themselves. Nobody here ever handles
   a password. */

/** Fails closed and never throws — the same shape isFeedbackAdmin() uses,
 *  because the quiet failure of a permission check has to be the safe way. */
export async function isAdmin() {
  if (!isConfigured || !currentUser()) return false;
  const { data, error } = await supabase.rpc('is_admin');
  if (error) { console.warn('[illusio] admin check failed', error.message); return false; }
  return data === true;
}

/** Every licence, newest first. Admin-only by RLS, so a non-admin gets an
 *  empty list rather than an error — which is why the caller should not read
 *  "empty" as "you are not an admin". */
export async function listLicences() {
  const { data, error } = await requireClient()
    .from('motion_licences')
    .select('email, expires_at, note, created_at, updated_at')
    .order('created_at', { ascending: false });
  if (error) {
    console.error('[illusio] could not list licences', error);
    throw new Error('Couldn’t load licences. Are you signed in as an admin?');
  }
  return data || [];
}

/**
 * Every account, newest first, each already carrying whatever licence its
 * address holds. Backed by admin_list_users() (0011) — auth.users is not
 * reachable from a browser and should not be, so this is the one narrow
 * window onto it.
 *
 * A non-admin gets an empty array rather than an error, because the function
 * guards with is_admin() internally. Same caveat as listLicences(): "empty"
 * is not proof of "not an admin".
 */
export async function listUsers() {
  const { data, error } = await requireClient().rpc('admin_list_users');
  if (error) {
    console.error('[illusio] could not list users', error);
    /* PGRST202 is PostgREST for "no such function": the migration has not been
       applied to this project yet. Worth saying, because the generic message
       sends you off checking permissions that are fine. */
    throw new Error(error.code === 'PGRST202'
      ? 'Accounts need migration 0011_admin_list_users.sql — run it in the SQL editor. Licences below are unaffected.'
      : 'Couldn’t load accounts. Are you signed in as an admin?');
  }
  return data || [];
}

/** How many anonymous sessions exist. Excluded from listUsers() by the query
 *  itself (0013) rather than filtered here, so the 500 cap counts customers
 *  instead of traffic — the bug that hid every real account. Still worth
 *  showing as a number. */
export async function anonCount() {
  const { data, error } = await requireClient().rpc('admin_anon_count');
  if (error) { console.warn('[illusio] anon count failed', error.message); return 0; }
  return Number(data) || 0;
}

/** How many of those are old enough and empty enough for the scheduled
 *  cleanup to take (0014/0015). Reads the same stale_anon_ids() the deletion
 *  reads, so this is the set that would go rather than an estimate of it —
 *  which is the only reason it is worth showing at all. */
export async function staleAnonCount() {
  const { data, error } = await requireClient().rpc('admin_stale_anon_count');
  if (error) { console.warn('[illusio] stale anon count failed', error.message); return null; }
  return Number(data) || 0;
}

/** Recent cleanup runs, newest first. Returns [] on failure rather than
 *  throwing: an unavailable history should not blank the account list beside
 *  it, and "no runs recorded" is itself the answer worth seeing. */
export async function cleanupLog(limit = 5) {
  const { data, error } = await requireClient()
    .rpc('admin_anon_cleanup_log', { p_limit: limit });
  if (error) { console.warn('[illusio] cleanup log failed', error.message); return []; }
  return data || [];
}

/**
 * Grant or amend one. Upsert rather than insert-or-update by hand: the email
 * is the primary key, so "grant to someone who already has one" is an
 * extension of their licence and not an error to report.
 *
 * @param {string} email
 * @param {string|null} expiresAt  ISO timestamp, or null for no expiry
 * @param {string} [note]
 */
/**
 * Set an account's credit balance to an exact number.
 *
 * SETS rather than adds, which is what the admin field asks for: "this
 * account should have this many". An adjustment would make the owner read the
 * current balance and do arithmetic to reach a total they could have typed.
 * The ledger still records the delta — see 0026.
 *
 * Keyed on email so it can be used straight from an invoice. The server
 * checks is_admin() itself; this is the polite half.
 */
export async function setCredits(email, balance) {
  const addr = String(email || '').trim().toLowerCase();
  if (!addr) throw new Error('An email address is needed.');
  const n = Number(balance);
  if (!Number.isInteger(n) || n < 0) throw new Error('Credits must be a whole number, zero or more.');

  const { data, error } = await requireClient()
    .rpc('admin_set_credits', { p_email: addr, p_balance: n });
  if (error) {
    console.error('[illusio] could not set credits', error);
    /* The server raises a readable message for the two cases worth telling
       apart — no account for that address, and not authorised. Anything else
       is a fault rather than a refusal. */
    throw new Error(/no account/i.test(error.message)
      ? 'No account for ' + addr + ' yet. They need to sign in once first.'
      : /not authorised/i.test(error.message)
        ? 'Not authorised. Are you signed in as an admin?'
        : 'Couldn’t set those credits.');
  }
  return data;
}

export async function setLicence(email, expiresAt, note = '') {
  const addr = String(email || '').trim().toLowerCase();
  if (!addr) throw new Error('An email address is needed.');
  const { error } = await requireClient()
    .from('motion_licences')
    .upsert({ email: addr, expires_at: expiresAt, note: note || null },
            { onConflict: 'email' });
  if (error) {
    console.error('[illusio] could not save licence', error);
    throw new Error('Couldn’t save that licence. Are you signed in as an admin?');
  }
}

/* ── the account itself ──────────────────────────────────────────────────────
   Creating, deleting and setting a password all need the service-role key,
   which cannot be in a browser — so they go through the admin-users Edge
   Function, which re-checks is_admin() against the CALLER's own token before
   it touches anything. See its header for the order that matters.

   invoke() attaches the current session's Authorization header for us, which
   is exactly the token that function verifies. */
async function adminAction(action, payload) {
  const { data, error } = await requireClient().functions.invoke('admin-users', {
    body: { action, ...payload }
  });
  /* Two failure shapes. A non-2xx arrives as `error` with the body unread, so
     the useful message — the one the function wrote — has to be dug out of
     the response rather than replaced with the generic one. */
  if (error) {
    let msg = '';
    try { msg = (await error.context?.json())?.error || ''; } catch (_) { /* not JSON */ }
    console.error('[illusio] admin ' + action + ' failed', error);
    throw new Error(msg || 'Couldn’t do that. Is the admin-users function deployed?');
  }
  if (data && data.error) throw new Error(data.error);
  return data;
}

/** Create an account with a password, already confirmed. */
export const createUser = (email, password) => adminAction('create', { email, password });

/** Everything that has happened to one address's licence (0023). Written by
 *  a trigger on motion_licences, so it covers grants made from either Admin
 *  tab and from the SQL editor alike — and it is the only record that
 *  survives a licence being removed, which deletes the row itself. */
export async function licenceHistory(email, limit = 50) {
  const { data, error } = await requireClient()
    .rpc('admin_licence_history', { p_email: email, p_limit: limit });
  if (error) { console.warn('[illusio] licence history failed', error.message); return []; }
  return data || [];
}

/** Tell somebody their Advanced trial is live, with its end date and an
 *  optional message from you. Called AFTER the licence is granted, never
 *  before — a mail failure must not cost them access they were promised, so
 *  the order is grant, then notify, and the two report separately. */
export const notifyLicence = (email, expiresAt, note) =>
  adminAction('licence_granted', { email, expiresAt, note: note || '' });

/** Set someone's password directly — for when email is not an option. */
export const setUserPassword = (email, password) => adminAction('set_password', { email, password });

/** Mark an address confirmed, for an account that never clicked its link. */
export const confirmUser = email => adminAction('confirm', { email });

/** Delete the account. Deliberately does NOT touch the licence: that is keyed
 *  on email and outlives the account by design (0010). */
export const deleteUser = email => adminAction('delete', { email });

export async function removeLicence(email) {
  const { error } = await requireClient()
    .from('motion_licences')
    .delete()
    .eq('email', email);
  if (error) {
    console.error('[illusio] could not remove licence', error);
    throw new Error('Couldn’t remove that licence.');
  }
}
